import React from "react";

import {
CheckCircle2,
Coffee,
ShieldCheck,
Clock3,
XCircle,
FileText,
Info,
} from "lucide-react";

import LineIcon from "./LineIcon";
import GuestDetailsForm from "./GuestDetailsForm";

/* =========================================================
FORMAT DATE
========================================================= */

const formatDate = (dateValue) => {
if (!dateValue) {
return "";
}

/*
 * Already in YYYY-MM-DD format.
 */
if (
    typeof dateValue === "string" &&
    /^\d{4}-\d{2}-\d{2}$/.test(dateValue)
) {
    return dateValue;
}

const date = new Date(dateValue);

if (Number.isNaN(date.getTime())) {
    return "";
}

const year = date.getFullYear();

const month = String(
    date.getMonth() + 1
).padStart(2, "0");

const day = String(
    date.getDate()
).padStart(2, "0");

return `${year}-${month}-${day}`;


};

/* =========================================================
GET STAY DATES

Example:

Check-in:  2026-09-03
Check-out: 2026-09-05

Charged nights:

2026-09-03
2026-09-04

Checkout date is NOT charged.
========================================================= */

const getStayDates = (
checkIn,
checkOut
) => {
const dates = [];

if (!checkIn || !checkOut) {
    return dates;
}

const formattedCheckIn =
    formatDate(checkIn);

const formattedCheckOut =
    formatDate(checkOut);

if (
    !formattedCheckIn ||
    !formattedCheckOut
) {
    return dates;
}

const start = new Date(
    `${formattedCheckIn}T00:00:00`
);

const end = new Date(
    `${formattedCheckOut}T00:00:00`
);

if (
    Number.isNaN(start.getTime()) ||
    Number.isNaN(end.getTime()) ||
    start >= end
) {
    return dates;
}

const current = new Date(start);

while (current < end) {
    dates.push(
        formatDate(current)
    );

    current.setDate(
        current.getDate() + 1
    );
}

return dates;


};

/* =========================================================
DETAILS PAGE
========================================================= */

const DetailsPage = ({
selectedRoom,
bookingDetails,
searchDetails,
guestForm,
onGuestChange,
adults,
children,
totalGuests,
roomPrice,
calculatedNights,
roomTotal,
taxRate,
taxAmount,
finalTotal,
bookingLoading,
setBookingStep,
}) => {


/* =====================================================
   FORMAT PRICE
===================================================== */

const formatPrice = (value) =>
    Number(
        value || 0
    ).toLocaleString(
        "en-IN"
    );


/* =====================================================
   STAY DATES

   Checkout date is excluded.
===================================================== */

const stayDates =
    getStayDates(
        searchDetails?.checkIn,
        searchDetails?.checkOut
    );


/* =====================================================
   NIGHTLY RATES

   Expected from parent:

   selectedRoom.nightlyRates = [

       {
           date: "2026-09-03",
           rate: 7000
       },

       {
           date: "2026-09-04",
           rate: 9000
       }

   ]
===================================================== */

const nightlyRates =
    Array.isArray(
        selectedRoom?.nightlyRates
    )
        ? selectedRoom.nightlyRates
        : [];


/* =====================================================
   CREATE DATE → RATE MAP

   Example:

   {
       "2026-09-03": 7000,
       "2026-09-04": 9000
   }
===================================================== */

const nightlyRateMap =
    nightlyRates.reduce(
        (
            map,
            item
        ) => {

            const date =
                formatDate(
                    item?.date
                );

            const rate =
                Number(
                    item?.rate ??
                    item?.newRate ??
                    item?.price ??
                    item?.finalRate ??
                    item?.amount ??
                    0
                );

            if (
                date &&
                Number.isFinite(rate)
            ) {

                map[date] =
                    rate;

            }

            return map;

        },
        {}
    );


/* =====================================================
   FALLBACK ROOM PRICE

   IMPORTANT:

   This is used ONLY when a date-wise rate
   does not exist.

   It should NOT normally be used when
   nightlyRates contains all selected dates.
===================================================== */

const fallbackRoomPrice =
    Number(
        selectedRoom?.price ??
        selectedRoom?.todayPrice ??
        selectedRoom?.channelManagerRate ??
        selectedRoom?.discountedPrice ??
        selectedRoom?.basePrice ??
        roomPrice ??
        0
    );


/* =====================================================
   BUILD NIGHTLY BREAKDOWN

   Example:

   2026-09-03 → ₹7000
   2026-09-04 → ₹9000
===================================================== */

const nightlyBreakdown =
    stayDates.map(
        (date) => {

            const dateRate =
                nightlyRateMap[
                    date
                ];

            return {

                date,

                rate:
                    dateRate !== undefined &&
                    dateRate !== null
                        ? Number(
                            dateRate
                        )
                        : fallbackRoomPrice,

            };

        }
    );


/* =====================================================
   ACTUAL ROOM TOTAL

   Example:

   ₹7000 + ₹9000

   = ₹16000

   DO NOT multiply a single rate
   by the number of nights.
===================================================== */

const calculatedRoomTotal =
    nightlyBreakdown.length > 0
        ? nightlyBreakdown.reduce(
            (
                total,
                night
            ) =>
                total +
                Number(
                    night.rate || 0
                ),
            0
        )
        : Number(
            roomTotal || 0
        );


/* =====================================================
   ORIGINAL PRICE

   Used for offer information.
===================================================== */

const originalPrice =
    Number(
        selectedRoom?.originalPrice ??
        selectedRoom?.todayPrice ??
        selectedRoom?.price ??
        selectedRoom?.basePrice ??
        roomPrice ??
        0
    );


/* =====================================================
   DISCOUNTED PRICE
===================================================== */

const discountedPrice =
    Number(
        selectedRoom?.discountedPrice ??
        roomPrice ??
        originalPrice
    );


/* =====================================================
   DISCOUNT
===================================================== */

const discountAmount =
    Number(
        selectedRoom?.discountAmount ??
        Math.max(
            originalPrice -
            discountedPrice,
            0
        )
    );


const hasDiscount =
    discountAmount > 0 &&
    discountedPrice < originalPrice;


const appliedOffer =
    selectedRoom?.appliedOffer ||
    null;


/* =====================================================
   TAX

   Tax is calculated from the
   actual date-wise room total.
===================================================== */

const calculatedTaxAmount =
    taxRate !== undefined &&
    taxRate !== null
        ? (
            calculatedRoomTotal *
            Number(
                taxRate || 0
            )
        ) / 100
        : Number(
            taxAmount || 0
        );


/* =====================================================
   FINAL TOTAL
===================================================== */

const calculatedFinalTotal =
    calculatedRoomTotal +
    calculatedTaxAmount;


/* =====================================================
   BREAKFAST
===================================================== */

const breakfastIncluded =
    selectedRoom?.breakfastIncluded ===
    true;


const breakfastType =
    selectedRoom?.breakfastType ||
    "";


/* =====================================================
   POLICIES
===================================================== */

const cancellationPolicy =
    selectedRoom?.cancellationPolicy ||
    "";


const termsAndConditions =
    selectedRoom?.termsAndConditions ||
    "";


const paymentPolicy =
    selectedRoom?.paymentPolicy ||
    "";


return (

    <div className="be-booking-page-grid">


        {/* =============================================
            LEFT COLUMN
        ============================================= */}

        <section className="be-booking-main-column">


            {/* GUEST DETAILS */}

            <div className="be-booking-card be-details-card">

                <div className="be-booking-card-heading be-compact-heading">

                    <div className="be-step-heading">

                        <span className="be-card-step">
                            02
                        </span>

                        <div>

                            <h3>
                                Enter your details
                            </h3>

                            <p>
                                We'll use these details to confirm your reservation.
                            </p>

                        </div>

                    </div>

                </div>


                <GuestDetailsForm
                    guestForm={guestForm}
                    onGuestChange={onGuestChange}
                    setBookingStep={setBookingStep}
                    bookingLoading={bookingLoading}
                />

            </div>


            {/* STAY INFORMATION */}

            <div className="be-booking-card be-stay-info-card">

                <div className="be-section-title">

                    <div className="be-section-title-icon">
                        <Info size={17} />
                    </div>

                    <div>

                        <h3>
                            Your stay
                        </h3>

                        <p>
                            Important information about this room
                        </p>

                    </div>

                </div>


                <div className="be-stay-info-grid">


                    {/* BREAKFAST */}

                    <div className="be-stay-info-item">

                        <div className="be-stay-info-icon">
                            <Coffee size={18} />
                        </div>

                        <div>

                            <span>
                                Breakfast
                            </span>

                            <strong
                                className={
                                    breakfastIncluded
                                        ? "be-policy-green"
                                        : "be-policy-muted"
                                }
                            >
                                {breakfastIncluded
                                    ? "Included"
                                    : "Not included"}
                            </strong>

                            {breakfastIncluded &&
                                breakfastType && (

                                    <small>

                                        {breakfastType ===
                                        "VEG"
                                            ? "Vegetarian breakfast"
                                            : breakfastType}

                                    </small>

                                )}

                        </div>

                    </div>


                    {/* CHECK-IN */}

                    <div className="be-stay-info-item">

                        <div className="be-stay-info-icon">
                            <Clock3 size={18} />
                        </div>

                        <div>

                            <span>
                                Check-in
                            </span>

                            <strong>
                                {selectedRoom?.checkInTime ||
                                    "--"}
                            </strong>

                        </div>

                    </div>


                    {/* CHECK-OUT */}

                    <div className="be-stay-info-item">

                        <div className="be-stay-info-icon">
                            <Clock3 size={18} />
                        </div>

                        <div>

                            <span>
                                Check-out
                            </span>

                            <strong>
                                {selectedRoom?.checkOutTime ||
                                    "--"}
                            </strong>

                        </div>

                    </div>

                </div>

            </div>


            {/* CANCELLATION POLICY */}

            <div className="be-booking-card be-policy-card">

                <div className="be-policy-header">

                    <div className="be-policy-title">

                        {selectedRoom?.freeCancellation ? (

                            <CheckCircle2
                                size={19}
                                className="be-policy-icon-green"
                            />

                        ) : (

                            <XCircle
                                size={19}
                                className="be-policy-icon-muted"
                            />

                        )}

                        <div>

                            <h3>
                                Cancellation policy
                            </h3>

                            {selectedRoom?.freeCancellation &&
                                selectedRoom?.freeCancellationHours && (

                                    <span className="be-free-cancellation">

                                        Free cancellation up to{" "}

                                        {
                                            selectedRoom
                                                .freeCancellationHours
                                        }{" "}

                                        hours before check-in

                                    </span>

                                )}

                        </div>

                    </div>

                </div>


                <div className="be-policy-content">

                    {cancellationPolicy
                        .split("\n")
                        .filter(Boolean)
                        .map(
                            (
                                line,
                                index
                            ) => (

                                <div
                                    className="be-policy-line"
                                    key={index}
                                >

                                    <span>
                                        {line.trim()}
                                    </span>

                                </div>

                            )
                        )}

                </div>

            </div>


            {/* TERMS & CONDITIONS */}

            <div className="be-booking-card be-policy-card">

                <div className="be-policy-header">

                    <div className="be-policy-title">

                        <FileText
                            size={19}
                            className="be-policy-icon"
                        />

                        <div>

                            <h3>
                                Terms & conditions
                            </h3>

                            <span>
                                Please review the property rules before booking.
                            </span>

                        </div>

                    </div>

                </div>


                <div className="be-policy-content">

                    {termsAndConditions
                        .split("\n")
                        .filter(Boolean)
                        .map(
                            (
                                line,
                                index
                            ) => (

                                <div
                                    className="be-policy-line"
                                    key={index}
                                >

                                    <span>
                                        {line.trim()}
                                    </span>

                                </div>

                            )
                        )}

                </div>


                {paymentPolicy && (

                    <div className="be-payment-policy">

                        <strong>
                            Payment policy
                        </strong>

                        <span>
                            {paymentPolicy}
                        </span>

                    </div>

                )}

            </div>


            {/* GOOD TO KNOW */}

            <div className="be-good-to-know be-good-to-know-compact">

                <div className="be-good-to-know-icon">

                    <LineIcon
                        type="info"
                        size={18}
                    />

                </div>

                <div>

                    <h3>
                        Good to know
                    </h3>

                    <p>
                        Please make sure your contact details are correct.
                        Your booking confirmation and important stay
                        information will be sent to the email address and
                        mobile number provided.
                    </p>

                </div>

            </div>

        </section>


        {/* =============================================
            RIGHT SUMMARY
        ============================================= */}

        <aside className="be-booking-summary-column">

            <div className="be-booking-summary-card be-summary-compact">


                {/* HEADER */}

                <div className="be-summary-heading">

                    <div>

                        <h3>
                            Reservation summary
                        </h3>

                        <span>
                            {selectedRoom?.name}
                        </span>

                    </div>

                </div>


                {/* ROOM IMAGE */}

                <div className="be-summary-room">

                    <img
                        src={
                            selectedRoom?.image ||
                            selectedRoom?.images?.[0]
                        }
                        alt={
                            selectedRoom?.name ||
                            "Room"
                        }
                    />

                    <div>

                        <strong>
                            {selectedRoom?.name}
                        </strong>

                        <span>
                            {selectedRoom?.type ||
                                selectedRoom?.viewType ||
                                "Room"}
                        </span>

                    </div>

                </div>


                {/* DATES */}

                <div className="be-summary-dates">

                    <div>

                        <span>
                            Check-in
                        </span>

                        <strong>
                            {searchDetails?.checkIn ||
                                "Not selected"}
                        </strong>

                    </div>


                    <div>

                        <span>
                            Check-out
                        </span>

                        <strong>
                            {searchDetails?.checkOut ||
                                "Not selected"}
                        </strong>

                    </div>

                </div>


                {/* GUESTS */}

                <div className="be-summary-line">

                    <span>
                        Guests
                    </span>

                    <strong>

                        {totalGuests}{" "}

                        {totalGuests === 1
                            ? "Guest"
                            : "Guests"}

                    </strong>

                </div>


                {/* =========================================
                    NIGHTLY RATE BREAKDOWN
                ========================================= */}

                {nightlyBreakdown.length > 0 && (

                    <div className="be-summary-nightly-rates">

                        <span className="be-summary-nightly-title">
                            Nightly rates
                        </span>


                        {nightlyBreakdown.map(
                            (night) => (

                                <div
                                    className="be-summary-price-row"
                                    key={night.date}
                                >

                                    <span>
                                        {night.date}
                                    </span>

                                    <strong>
                                        ₹{formatPrice(
                                            night.rate
                                        )}
                                    </strong>

                                </div>

                            )
                        )}

                    </div>

                )}


                {/* OFFER */}

                {hasDiscount && (

                    <div className="be-summary-offer">

                        <div className="be-summary-offer-top">

                            <CheckCircle2
                                size={16}
                            />

                            <strong>
                                Special offer applied
                            </strong>

                        </div>


                        {appliedOffer?.name && (

                            <span>
                                {appliedOffer.name}
                            </span>

                        )}

                    </div>

                )}


                {/* =========================================
                    PRICE SUMMARY
                ========================================= */}

                <div className="be-summary-price-section">


                    {/* ROOM PRICE */}

                    <div className="be-summary-price-row">

                        <span>
                            Room price
                        </span>

                        <strong>
                            ₹{formatPrice(
                                calculatedRoomTotal
                            )}
                        </strong>

                    </div>


                    {/* STAY */}

                    <div className="be-summary-price-row">

                        <span>
                            Stay
                        </span>

                        <span className="be-summary-price-muted">

                            {nightlyBreakdown.length}{" "}

                            {nightlyBreakdown.length === 1
                                ? "night"
                                : "nights"}

                        </span>

                    </div>


                    {/* TAX */}

                    <div className="be-summary-price-row">

                        <span>
                            Taxes & charges
                        </span>

                        <strong>
                            ₹{formatPrice(
                                calculatedTaxAmount
                            )}
                        </strong>

                    </div>


                    {/* GST */}

                    <div className="be-summary-price-row">

                        <span>
                            GST
                        </span>

                        <span className="be-summary-price-muted">
                            {taxRate || 0}%
                        </span>

                    </div>


                    {/* FINAL TOTAL */}

                    <div className="be-summary-total">

                        <span>
                            Total price
                        </span>

                        <strong>
                            ₹{formatPrice(
                                calculatedFinalTotal
                            )}
                        </strong>

                    </div>

                </div>


                {/* BREAKFAST */}

                <div className="be-summary-breakfast">

                    <Coffee size={16} />

                    <span>

                        {breakfastIncluded
                            ? `Breakfast included${
                                breakfastType === "VEG"
                                    ? " · Vegetarian"
                                    : ""
                            }`
                            : "Breakfast not included"
                        }

                    </span>

                </div>


                {/* CANCELLATION */}

                <div className="be-summary-cancellation">

                    {selectedRoom?.freeCancellation ? (

                        <CheckCircle2
                            size={16}
                        />

                    ) : (

                        <XCircle
                            size={16}
                        />

                    )}

                    <span>

                        {selectedRoom?.freeCancellation
                            ? "Free cancellation available"
                            : "Cancellation policy applies"
                        }

                    </span>

                </div>


                {/* NOTE */}

                <div className="be-summary-note">

                    <LineIcon
                        type="info"
                        size={15}
                    />

                    <span>
                        Final taxes and applicable charges are included in
                        the total shown above.
                    </span>

                </div>

            </div>


            {/* SECURE BOOKING */}

            <div className="be-booking-help-card">

                <ShieldCheck size={20} />

                <div>

                    <strong>
                        Secure booking
                    </strong>

                    <p>
                        Your booking details are handled securely.
                    </p>

                </div>

            </div>

        </aside>

    </div>

);

};

export default DetailsPage;
